/**
 * Payment Means Codes according to UN/CEFACT Code List 4461
 * Source: UNTDID 4461 - Payment means code
 * Used in XRechnung, ZUGFeRD, and other electronic invoice formats
 */

export interface PaymentMeansInfo {
  code: string;              // Official payment means code
  name: string;              // English name
  germanName: string;        // German name
  description: string;       // Detailed description
  category: string;          // Category (electronic, cash, etc.)
  isCommon: boolean;         // Commonly used in EU invoicing
  isActive: boolean;         // Currently active
  requiresAccount: boolean;  // Requires account information
  requiresBIC: boolean;      // Requires BIC/SWIFT code
  examples: string[];        // Example use cases
}

export const PAYMENT_MEANS_DATA: Record<string, PaymentMeansInfo> = {
  // Electronic Transfer Methods
  '30': {
    code: '30',
    name: 'Credit transfer',
    germanName: 'Überweisung',
    description: 'Credit transfer to the financial institution account',
    category: 'Electronic Transfer',
    isCommon: true,
    isActive: true,
    requiresAccount: true,
    requiresBIC: true,
    examples: ['Bank transfer', 'Wire transfer', 'International transfer']
  },
  
  '58': {
    code: '58',
    name: 'SEPA credit transfer',
    germanName: 'SEPA-Überweisung',
    description: 'Single Euro Payments Area credit transfer',
    category: 'Electronic Transfer',
    isCommon: true,
    isActive: true,
    requiresAccount: true,
    requiresBIC: false,
    examples: ['SEPA transfer', 'Euro area transfer', 'EU bank transfer']
  },
  
  // Direct Debit Methods
  '49': {
    code: '49',
    name: 'Direct debit',
    germanName: 'Lastschrift',
    description: 'Direct debit from the customer account',
    category: 'Direct Debit',
    isCommon: true,
    isActive: true,
    requiresAccount: true,
    requiresBIC: true,
    examples: ['Direct debit', 'Automatic payment', 'Recurring payment']
  },
  
  '59': {
    code: '59',
    name: 'SEPA direct debit',
    germanName: 'SEPA-Lastschrift',
    description: 'Single Euro Payments Area direct debit',
    category: 'Direct Debit',
    isCommon: true,
    isActive: true,
    requiresAccount: true,
    requiresBIC: false,
    examples: ['SEPA direct debit', 'Euro area direct debit', 'EU recurring payment']
  },
  
  // Card Payments
  '48': {
    code: '48',
    name: 'Bank card',
    germanName: 'Bankkarte',
    description: 'Payment by bank card',
    category: 'Card Payment',
    isCommon: true,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Debit card', 'Credit card', 'EC card', 'Card payment']
  },
  
  '54': {
    code: '54',
    name: 'Credit card',
    germanName: 'Kreditkarte',
    description: 'Payment by credit card',
    category: 'Card Payment',
    isCommon: true,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Visa', 'Mastercard', 'American Express', 'Credit card payment']
  },
  
  // Cash and Check
  '10': {
    code: '10',
    name: 'In cash',
    germanName: 'Bar',
    description: 'Payment in cash',
    category: 'Cash',
    isCommon: true,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Cash payment', 'Cash on delivery', 'Cash transaction']
  },
  
  '20': {
    code: '20',
    name: 'Cheque',
    germanName: 'Scheck',
    description: 'Payment by cheque',
    category: 'Check',
    isCommon: false,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Bank cheque', 'Personal cheque', 'Certified cheque']
  },
  
  // Digital and Online Payments
  '68': {
    code: '68',
    name: 'Online payment service',
    germanName: 'Online-Zahlungsdienst',
    description: 'Payment through online payment service',
    category: 'Digital Payment',
    isCommon: true,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['PayPal', 'Klarna', 'Stripe', 'Online payment gateway']
  },
  
  '69': {
    code: '69',
    name: 'Transfer and online payment',
    germanName: 'Überweisung und Online-Zahlung',
    description: 'Combination of transfer and online payment',
    category: 'Digital Payment',
    isCommon: true,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Sofortüberweisung', 'Giropay', 'iDEAL', 'Online banking']
  },
  
  // Other Methods
  '1': {
    code: '1',
    name: 'Instrument not defined',
    germanName: 'Zahlungsmittel nicht definiert',
    description: 'Payment instrument not defined',
    category: 'Other',
    isCommon: false,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Unspecified payment method', 'To be determined']
  },
  
  '31': {
    code: '31',
    name: 'Debit transfer',
    germanName: 'Lastschrifteinzug',
    description: 'Debit transfer from the customer account',
    category: 'Electronic Transfer',
    isCommon: false,
    isActive: true,
    requiresAccount: true,
    requiresBIC: true,
    examples: ['Debit transfer', 'Account debit']
  },
  
  '42': {
    code: '42',
    name: 'Payment to bank account',
    germanName: 'Zahlung auf Bankkonto',
    description: 'Payment directly to bank account',
    category: 'Electronic Transfer',
    isCommon: true,
    isActive: true,
    requiresAccount: true,
    requiresBIC: true,
    examples: ['Direct bank payment', 'Account transfer']
  },
  
  // Standing Order
  '53': {
    code: '53',
    name: 'Urgent credit transfer',
    germanName: 'Eilüberweisung',
    description: 'Urgent credit transfer',
    category: 'Electronic Transfer',
    isCommon: false,
    isActive: true,
    requiresAccount: true,
    requiresBIC: true,
    examples: ['Express transfer', 'Same-day transfer', 'Urgent payment']
  },
  
  // Mobile Payment
  '70': {
    code: '70',
    name: 'Mobile payment',
    germanName: 'Mobile Zahlung',
    description: 'Payment via mobile device',
    category: 'Digital Payment',
    isCommon: true,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Apple Pay', 'Google Pay', 'Samsung Pay', 'Mobile wallet']
  },
  
  // Cryptocurrency (emerging)
  '97': {
    code: '97',
    name: 'Clearing between partners',
    germanName: 'Verrechnung zwischen Partnern',
    description: 'Clearing or offsetting between business partners',
    category: 'Other',
    isCommon: false,
    isActive: true,
    requiresAccount: false,
    requiresBIC: false,
    examples: ['Business partner clearing', 'Offset payment', 'Mutual settlement']
  }
};

// Common payment means for prioritized display
export const COMMON_PAYMENT_MEANS = ['30', '58', '49', '59', '48', '10', '68', '69', '70'] as const;

// SEPA payment means (for EU invoicing)
export const SEPA_PAYMENT_MEANS = ['58', '59'] as const;

// Payment means categories for organized display
export const PAYMENT_MEANS_CATEGORIES = {
  'Electronic Transfer': ['30', '58', '31', '42', '53'],
  'Direct Debit': ['49', '59'],
  'Card Payment': ['48', '54'],
  'Digital Payment': ['68', '69', '70'],
  'Traditional': ['10', '20'],
  'Other': ['1', '97']
} as const;

// Get all active payment means codes
export const getAllPaymentMeansCodes = (): string[] => {
  return Object.keys(PAYMENT_MEANS_DATA).filter(code => PAYMENT_MEANS_DATA[code].isActive);
};

// Get payment means info by code
export const getPaymentMeansInfo = (code: string): PaymentMeansInfo | undefined => {
  return PAYMENT_MEANS_DATA[code];
};

// Get payment means name by code
export const getPaymentMeansName = (code: string): string => {
  const means = getPaymentMeansInfo(code);
  return means?.name || code;
};

// Get German payment means name by code
export const getPaymentMeansGermanName = (code: string): string => {
  const means = getPaymentMeansInfo(code);
  return means?.germanName || code;
};

// Get formatted payment means display (code + name)
export const getFormattedPaymentMeansDisplay = (code: string): string => {
  const means = getPaymentMeansInfo(code);
  return means ? `${code} - ${means.name}` : code;
};

// Get formatted payment means display with German name
export const getFormattedPaymentMeansDisplayGerman = (code: string): string => {
  const means = getPaymentMeansInfo(code);
  return means ? `${code} - ${means.germanName}` : code;
};

// Check if payment means requires account information
export const requiresAccountInfo = (code: string): boolean => {
  const means = getPaymentMeansInfo(code);
  return means?.requiresAccount || false;
};

// Check if payment means requires BIC
export const requiresBIC = (code: string): boolean => {
  const means = getPaymentMeansInfo(code);
  return means?.requiresBIC || false;
};

// Check if payment means is SEPA
export const isSEPAPayment = (code: string): boolean => {
  return SEPA_PAYMENT_MEANS.includes(code as any);
};

// Get payment means by category
export const getPaymentMeansByCategory = (category: keyof typeof PAYMENT_MEANS_CATEGORIES): PaymentMeansInfo[] => {
  const paymentCodes = PAYMENT_MEANS_CATEGORIES[category];
  return paymentCodes.map(code => PAYMENT_MEANS_DATA[code]).filter(Boolean);
};

// Search payment means by name, German name, or code
export const searchPaymentMeans = (query: string): PaymentMeansInfo[] => {
  const searchTerm = query.toLowerCase();
  return Object.values(PAYMENT_MEANS_DATA)
    .filter(means => 
      means.isActive && (
        means.name.toLowerCase().includes(searchTerm) ||
        means.germanName.toLowerCase().includes(searchTerm) ||
        means.code.toLowerCase().includes(searchTerm) ||
        means.description.toLowerCase().includes(searchTerm)
      )
    )
    .sort((a, b) => {
      // Prioritize exact matches
      const aExact = a.code.toLowerCase() === searchTerm;
      const bExact = b.code.toLowerCase() === searchTerm;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Then prioritize common payment means
      const aCommon = a.isCommon;
      const bCommon = b.isCommon;
      
      if (aCommon && !bCommon) return -1;
      if (!aCommon && bCommon) return 1;
      
      // Finally alphabetical
      return a.name.localeCompare(b.name);
    });
};

// Get suggested payment means for specific contexts
export const getSuggestedPaymentMeans = (context: 'b2b' | 'b2c' | 'sepa' | 'international' = 'b2b'): string[] => {
  switch (context) {
    case 'b2b':
      return ['30', '58', '49', '59', '48'];
    case 'b2c':
      return ['48', '68', '69', '70', '10', '58'];
    case 'sepa':
      return ['58', '59'];
    case 'international':
      return ['30', '48', '54', '68'];
    default:
      return COMMON_PAYMENT_MEANS.slice();
  }
};

// Validate payment means code
export const isValidPaymentMeans = (code: string): boolean => {
  return PAYMENT_MEANS_DATA.hasOwnProperty(code) && PAYMENT_MEANS_DATA[code].isActive;
};

// Get payment means that support recurring payments
export const getRecurringPaymentMeans = (): string[] => {
  return Object.keys(PAYMENT_MEANS_DATA).filter(code => 
    PAYMENT_MEANS_DATA[code].category === 'Direct Debit' || 
    PAYMENT_MEANS_DATA[code].category === 'Digital Payment'
  );
};

export type CommonPaymentMeans = typeof COMMON_PAYMENT_MEANS[number];
export type AllPaymentMeans = keyof typeof PAYMENT_MEANS_DATA;
export type PaymentMeansCategory = keyof typeof PAYMENT_MEANS_CATEGORIES;